import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { minBy } from '@ps-gui/shared-utils';

@Injectable()
export class PageTracker {
  private readonly ngZone = inject(NgZone);

  private readonly destroyRef = inject(DestroyRef);

  private readonly visiblePages = new Map<string, Element>();

  private readonly _closestPageToPreviewCenter = signal<string | undefined>(undefined);

  readonly closestPageToPreviewCenter = this._closestPageToPreviewCenter.asReadonly();

  private intersectionRoot: Element | undefined;

  private removeScrollListener: (() => void) | undefined;

  private scheduledFrame: number | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.removeScrollListener?.();
      if (this.scheduledFrame !== undefined) {
        cancelAnimationFrame(this.scheduledFrame);
      }
    });
  }

  onPageBecameVisible(pageId: string, target: Element) {
    this.visiblePages.set(pageId, target);
    this.scheduleUpdate();
  }

  onPageBecameHidden(pageId: string) {
    this.visiblePages.delete(pageId);
    this.scheduleUpdate();
  }

  registerIntersectionRoot(root: Element) {
    if (root === this.intersectionRoot) {
      return;
    }

    this.removeScrollListener?.();
    this.intersectionRoot = root;

    const onScroll = () => this.scheduleUpdate();
    // zone.js runs a listener in the zone it was registered in - inside Angular every scroll event would trigger an app tick.
    this.ngZone.runOutsideAngular(() =>
      root.addEventListener('scroll', onScroll, { passive: true }),
    );
    this.removeScrollListener = () => root.removeEventListener('scroll', onScroll);

    this.scheduleUpdate();
  }

  private scheduleUpdate() {
    if (this.scheduledFrame !== undefined) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.scheduledFrame = requestAnimationFrame(() => {
        this.scheduledFrame = undefined;
        this.updateClosestPage();
      });
    });
  }

  private updateClosestPage() {
    if (!this.intersectionRoot || this.visiblePages.size === 0) {
      return;
    }

    if (this.visiblePages.size === 1) {
      this._closestPageToPreviewCenter.set(this.visiblePages.keys().next().value);
      return;
    }

    this.selectClosestPage(this.intersectionRoot);
  }

  private getCenterY(element: Element) {
    const rect = element.getBoundingClientRect();
    return (rect.bottom + rect.top) * 0.5;
  }

  private selectClosestPage(root: Element) {
    const pageCenters = this.visiblePages
      .entries()
      .map(([pageId, target]) => [pageId, this.getCenterY(target)] as const);

    const rootCenter = this.getCenterY(root);

    const closestPageCenter = minBy([...pageCenters], ([_, center]) =>
      Math.abs(center - rootCenter),
    );

    this._closestPageToPreviewCenter.set(closestPageCenter?.[0]);
  }
}
